import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Skeleton } from '@mui/material';

import { ROUTES as SOURCE_ROUTES } from 'theSource/app/routes';
import { Logo } from 'theSource/components/Logo';
import { EnrollmentNavigation } from './components/EnrollmentNavigation';
import {
  EnrollmentNavVisibleStyled,
  HeaderPerPayPeriodBoxStyled,
  TabletChatBotStyling,
  TotalCostStyled,
} from './HeaderStyled';
import { MobileStepper } from './components/MobileStepper';
import { GET_ENROLLMENT_CONTEXT_QUERY_KEY } from 'theSource/queries';
import { PSModalWrapper } from '../Shared';
import { CartDrawer } from './components/CartDrawer/CartDrawer';
import { queryClient } from 'theSource/utils';
import {
  selectIsLoadingBenefits,
  selectPerPayPeriodCost,
} from './components/EnrollmentNavigation/services';
import { PsButtonTuner } from 'stencilSetup';
import { OliveBanner } from 'enrollment/features/OliveAdvisor/OliveBanner';
import { ChatBot } from 'components/ChatBot';
import {
  selectDisplayPostElectionToast,
  selectToastBenefitLookupCode,
  selectPerPayPeriodText,
  setDisplayToast,
  setToastBenefitLookupCode,
} from 'enrollment/services';
import { getPremiumStyled, RETURN_DISPLAY } from 'enrollment/utils';
import { ResetEnrollmentModal } from 'features/HomePage/components/MainNavigation/ResetEnrollmentModal';
import { selectIsAdmin } from 'theSource/features';
import { AdminOptions } from './components/AdminOptions';
import { PostElectionToast } from '../PostElectionToast';

export const Header = ({ chatBot }: { chatBot?: boolean }) => {
  const [cartDrawerDetails, setCartDrawerDetails] = useState(false);

  const location = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isAdmin = useSelector(selectIsAdmin);
  const perPayPeriodCost = useSelector(selectPerPayPeriodCost);
  const perPayText = useSelector(selectPerPayPeriodText);
  const displayToast = useSelector(selectDisplayPostElectionToast);
  const benefitLookupCode = useSelector(selectToastBenefitLookupCode);

  const closeBtnOtherData = [
    { attribute: 'data-bs-toggle', value: 'modal' },
    { attribute: 'data-bs-target', value: '#closeEnrollment-popover' },
  ];

  const isLoadingBenefitsData = useSelector(selectIsLoadingBenefits);

  const navTopath = () => {
    queryClient.invalidateQueries(GET_ENROLLMENT_CONTEXT_QUERY_KEY);
    navigate(SOURCE_ROUTES.main.home.path);
  };

  useEffect(() => {
    document
      .querySelector('button[id^="closeEnrollment-popover"][id$="_primary"]')
      ?.addEventListener('click', () => navTopath());
  });

  useEffect(() => {
    return () => {
      dispatch(setDisplayToast(false));
      dispatch(setToastBenefitLookupCode(''));
    };
  });

  // Check if the current route is for Open Enrollment
  const isOnOpenEnrollmentPage =
    location.pathname.endsWith('/enrollment/') ||
    location.pathname.endsWith('/enrollment');

  useEffect(() => {
    if (displayToast) {
      setTimeout(() => {
        dispatch(setDisplayToast(false));
      }, 6200);
    }
  }, [displayToast, dispatch]);

  // If on Open Enrollment page, don't render the header
  if (isOnOpenEnrollmentPage) {
    return null;
  }

  const isSpokePage = location.pathname.includes('/plans');

  const handleCartDrawer = () => {
    const infoDialog = document?.querySelector('#ps-cart-drawer');
    document?.dispatchEvent(
      new CustomEvent('psOpenDialog', { detail: infoDialog })
    );
    setCartDrawerDetails(!cartDrawerDetails);
  };

  const isDevice = window.innerWidth < 1200;
  const isScreenSmall = window.innerWidth < 744;

  return (
    <>
      <header
        className="fixed-top header header-container row mx-0 my-0 py-0"
        data-testid="enrollment-header"
      >
        <EnrollmentNavVisibleStyled
          className="v-center col-3 col-md-4 col-lg-3 order-md-first"
          aria-label={t('common:returnToHome')}
        >
          <PsButtonTuner
            buttonId="back-button-icon"
            label="Close"
            level="tertiary"
            isIcon={true}
            icon="x"
            extraClass="blackIcon mr-3"
            otherData={closeBtnOtherData}
          />
          <div className="logo">
            <Logo />
          </div>
        </EnrollmentNavVisibleStyled>
        <EnrollmentNavigation chatBot={chatBot}></EnrollmentNavigation>
        <HeaderPerPayPeriodBoxStyled className="d-flex col-3 col-md-4 col-lg-3 order-lg-last order-sm-first">
          <TabletChatBotStyling className="my-auto">
            {chatBot && <ChatBot renderDefault={false} showText={false} />}
          </TabletChatBotStyling>
          {isAdmin && !isScreenSmall && (
            <div className="d-flex align-items-center mr-3">
              <AdminOptions />
            </div>
          )}
          {isLoadingBenefitsData ? (
            <div
              className="d-flex flex-column"
              style={{ maxWidth: '120px', height: '80px' }}
            >
              <Skeleton
                width="120px"
                height="48px"
                role="status"
                aria-label="Loading..."
              />
              <Skeleton
                width="120px"
                height="16px"
                role="status"
                aria-label="Loading..."
              />
            </div>
          ) : (
            <TotalCostStyled
              cartDrawerDetails={false}
              data-testid="per-pay-period"
              className="toast-cart-target"
            >
              <Button
                type="button"
                className="py-0 d-flex flex-column h-100 text-nowrap"
                onClick={handleCartDrawer}
                id="cart-drawer-button"
                aria-label={
                  t('currentTotal') +
                  '$' +
                  String(perPayPeriodCost) +
                  t('currentTotalEnd')
                }
                tabIndex={0}
              >
                <span aria-live="polite" id="running-total">
                  {getPremiumStyled({
                    premium: String(perPayPeriodCost),
                    valueOnly: RETURN_DISPLAY,
                    perPayText: perPayText,
                    tagName: 'div',
                  })}{' '}
                </span>
              </Button>

              <CartDrawer setCartDrawerDetails={setCartDrawerDetails} />
            </TotalCostStyled>
          )}
        </HeaderPerPayPeriodBoxStyled>
      </header>
      {!isDevice && <OliveBanner />}
      {!isSpokePage && <MobileStepper />}
      {isSpokePage && <div className="header-adjustment" />}

      <PSModalWrapper
        id="closeEnrollment-popover"
        message={t('closeNoticePopup')}
        title={t('closeEnrollment')}
        primaryButton={t('saveAndClose')}
        primaryActive={true}
        secondaryButton={t('cancel')}
        extraClass="text-left"
      ></PSModalWrapper>
      {displayToast && benefitLookupCode && (
        <PostElectionToast benefitLookupCode={benefitLookupCode} />
      )}
      <ResetEnrollmentModal />
    </>
  );
};
